// Plantele din jardinierele balcoanelor, pe machetă.
//
// O jardinieră (`Railing` de fel `planter`, format 3.5 §23) e un jgheab pe
// marginea balconului; aici i se pune pământul și ce crește în el. E DECOR:
// documentul spune doar «jardinieră» și ce fel de plantare (`planting`) — fără
// irigare, fără deviz, fără bilanț de spațiu verde (Cătălin, 5.10.2026).
//
// «Modele de plante mai simple, dar totuși realiste»: nu bulgări ca pomii
// machetei (`copaci3d.ts`), ci FRUNZE — fiecare plantă e făcută din zeci de
// frunze mici, cu nuanța ei, în jurul unui miez întunecat. Trei specii, fiecare
// desenată o singură dată și instanțiată, ca o mie de jardiniere să însemne
// trei apeluri de desenare:
//
//   tufa        o tufă rotundă, deasă (buxus, lavandă fără floare)
//   iarba       un smoc de iarbă ornamentală, cu câteva spice
//   curgatoare  o plantă care se revarsă peste buza jgheabului, spre afară
//
// Totul e determinist: specia, rotația, mărimea și nuanța ies din poziție.
import * as THREE from "three";

export type SpeciePlanta = "tufa" | "iarba" | "curgatoare";
export const SPECII_PLANTE: SpeciePlanta[] = ["tufa", "iarba", "curgatoare"];

export interface PlantaSit {
  x: number; y: number; z: number;
  sp: SpeciePlanta;
  /** rotația în jurul verticalei; la `curgatoare`, direcția SPRE AFARĂ */
  rot: number;
  /** mărimea (1 = planta de bază, ~45 cm) */
  s: number;
  /** numai la `curgatoare`: cât e de lat jgheabul peste care se revarsă */
  gros?: number;
}

/** Lățimea de jgheab pentru care e desenată `curgatoare`. */
const GROS_DE_BAZA = 0.4;
/** La câți metri stă o plantă de următoarea, în lungul jardinierei. */
export const PAS_PLANTE_M = 0.45;
/** Cu cât stă pământul peste buza jgheabului (vezi `geometriePamant`). */
export const PAMANT_PESTE_M = 0.02;

function zar(sare: number): () => number {
  let a = sare >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashLoc(x: number, y: number, z: number, sare: number): number {
  let h = Math.imul(Math.round(x * 20), 0x27d4eb2d) ^ Math.imul(Math.round(y * 20), 0x165667b1)
        ^ Math.imul(Math.round(z * 20), 0x9e3779b1) ^ sare;
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h ^= h >>> 13;
  return (h >>> 0) / 4294967296;
}

// --------------------------------------------------------------------------- //
// geometrie: triunghiuri cu culoare pe vârf
// --------------------------------------------------------------------------- //

class Plasa {
  pos: number[] = [];
  col: number[] = [];
  private c = new THREE.Color();

  tri(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3, culoare: THREE.Color) {
    this.pos.push(a.x, a.y, a.z, b.x, b.y, b.z, c.x, c.y, c.z);
    for (let i = 0; i < 3; i++) this.col.push(culoare.r, culoare.g, culoare.b);
  }

  /** O FRUNZĂ: UN triunghi — lat la bază, ascuțit la vârf, pe `dir` —, mai
   *  închis la bază și deschis la vârf. Un romb din două triunghiuri arăta la
   *  fel de la zece metri și costa dublu: cu toate balcoanele plantate, macheta
   *  unui ansamblu trecea de 5 milioane de triunghiuri și de 70 ms pe cadru
   *  (măsurat pe Test-Catalin-v1, 5.10.2026). */
  frunza(p: THREE.Vector3, dir: THREE.Vector3, lat: THREE.Vector3, lung: number, latime: number,
         culoare: THREE.Color) {
    const a = p.clone().addScaledVector(lat, latime / 2);
    const b = p.clone().addScaledVector(lat, -latime / 2);
    const varf = p.clone().addScaledVector(dir, lung);
    this.pos.push(a.x, a.y, a.z, b.x, b.y, b.z, varf.x, varf.y, varf.z);
    this.c.copy(culoare).multiplyScalar(0.62);
    this.col.push(this.c.r, this.c.g, this.c.b, this.c.r, this.c.g, this.c.b,
                  culoare.r, culoare.g, culoare.b);
  }

  geometrie(): THREE.BufferGeometry {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(this.pos, 3));
    g.setAttribute("color", new THREE.Float32BufferAttribute(this.col, 3));
    g.computeVertexNormals();
    return g;
  }
}

const VERDE_INCHIS = new THREE.Color(0x27421f);
const perp = (v: THREE.Vector3, r: () => number) => {
  const a = Math.abs(v.z) < 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);
  return new THREE.Vector3().crossVectors(v, a).normalize()
    .applyAxisAngle(v.clone().normalize(), r() * Math.PI * 2);
};

/** TUFA: un miez întunecat și frunze mici pe tot conturul lui, mai deschise
 *  spre vârf și spre afară. ~45 cm lată, ~42 cm înaltă. */
function geometrieTufa(): THREE.BufferGeometry {
  const r = zar(0x7fa51);
  const m = new Plasa();
  // miezul: un octaedru mic, aproape negru-verde — umbra din mijlocul tufei
  const v = [[0.1, 0], [0, 0.1], [-0.1, 0], [0, -0.1]].map(([x, y]) => new THREE.Vector3(x, y, 0.19));
  const sus = new THREE.Vector3(0, 0, 0.3), jos = new THREE.Vector3(0, 0, 0.03);
  for (let i = 0; i < 4; i++) {
    m.tri(v[i], v[(i + 1) % 4], sus, VERDE_INCHIS);
    m.tri(v[(i + 1) % 4], v[i], jos, VERDE_INCHIS);
  }
  const jos_ = new THREE.Color(0x3a6229), sus_ = new THREE.Color(0x93b858), c = new THREE.Color();
  const O = new THREE.Vector3(0, 0, 0.19);
  // două straturi: cel dinăuntru, închis, umple; cel de afară dă conturul
  for (const [nr, raza, lung, latime, lumina] of [[18, 0.11, 0.1, 0.075, 0.0], [50, 0.17, 0.13, 0.085, 0.3]]) {
    for (let i = 0; i < nr; i++) {
      const u = r() * Math.PI * 2, t = Math.acos(1 - r() * 1.6);
      const n = new THREE.Vector3(Math.sin(t) * Math.cos(u), Math.sin(t) * Math.sin(u), Math.cos(t));
      const p = O.clone().addScaledVector(n, raza * (0.85 + r() * 0.3));
      // frunza pleacă din contur spre afară și puțin în sus, fiecare altfel răsucită
      const dir = n.clone().add(new THREE.Vector3(0, 0, 0.45)).add(perp(n, r).multiplyScalar(0.45)).normalize();
      const inalt = THREE.MathUtils.clamp((p.z - 0.04) / 0.36, 0, 1);
      c.copy(jos_).lerp(sus_, THREE.MathUtils.clamp(inalt * 0.6 + lumina + (r() - 0.5) * 0.3, 0, 1));
      m.frunza(p, dir, perp(dir, r), lung * (0.8 + r() * 0.4), latime, c);
    }
  }
  return m.geometrie();
}

/** IARBA: fire arcuite spre afară, îngustate la vârf; câteva spice mai înalte,
 *  cu vârful mov-cenușiu. ~55 cm înaltă. */
function geometrieIarba(): THREE.BufferGeometry {
  const r = zar(0x1a2ba);
  const m = new Plasa();
  const baza = new THREE.Color(0x4f7a31), varf = new THREE.Color(0xa9c56a), spic = new THREE.Color(0x8d7fa8);
  const c0 = new THREE.Color(), c1 = new THREE.Color();
  for (let i = 0; i < 15; i++) {
    const e_spic = i % 5 === 4;
    const u = r() * Math.PI * 2;
    const afara = new THREE.Vector3(Math.cos(u), Math.sin(u), 0);
    const lat = new THREE.Vector3(-afara.y, afara.x, 0);
    const h = (e_spic ? 0.56 : 0.34) + r() * 0.14;
    const aplecare = (e_spic ? 0.06 : 0.14) + r() * 0.12;
    const w = e_spic ? 0.014 : 0.026;
    let p0 = afara.clone().multiplyScalar(0.03 + r() * 0.03);
    c0.copy(baza);
    const N = 3;
    for (let k = 1; k <= N; k++) {
      const t = k / N;
      // firul urcă și se apleacă tot mai mult spre afară
      const p1 = afara.clone().multiplyScalar(0.04 + aplecare * t * t)
        .add(new THREE.Vector3(0, 0, h * (t - 0.18 * t * t)));
      c1.copy(baza).lerp(e_spic && k === N ? spic : varf, t);
      const w0 = w * (1 - (k - 1) / N), w1 = w * (1 - t);
      const a = p0.clone().addScaledVector(lat, w0), b = p0.clone().addScaledVector(lat, -w0);
      const d = p1.clone().addScaledVector(lat, w1), e = p1.clone().addScaledVector(lat, -w1);
      m.tri(a, b, d, c0);
      if (k < N) m.tri(b, e, d, c1);
      p0 = p1; c0.copy(c1);
    }
  }
  return m.geometrie();
}

/** CURGĂTOAREA: o movilă joasă de frunze și lujeri care trec peste buza
 *  jgheabului (spre +x) și atârnă 35–80 cm. Desenată pentru un jgheab de
 *  `GROS_DE_BAZA`; pe unul mai lat se întinde pe x. */
function geometrieCurgatoare(): THREE.BufferGeometry {
  const r = zar(0xc0a6a);
  const m = new Plasa();
  const jos_ = new THREE.Color(0x36632b), sus_ = new THREE.Color(0x7fae4c), c = new THREE.Color();
  // movila
  for (let i = 0; i < 16; i++) {
    const u = r() * Math.PI * 2, rr = Math.sqrt(r()) * 0.17;
    const p = new THREE.Vector3(Math.cos(u) * rr, Math.sin(u) * rr * 1.25, 0.02 + (0.17 - rr) * 0.45);
    const dir = new THREE.Vector3(Math.cos(u) * 0.7, Math.sin(u) * 0.7, 0.55 + r() * 0.4).normalize();
    c.copy(jos_).lerp(sus_, 0.35 + r() * 0.6);
    m.frunza(p, dir, perp(dir, r), 0.1 + r() * 0.04, 0.075, c);
  }
  // lujerii: peste buză la x = GROS/2 + 3 cm, apoi în jos
  const buza = GROS_DE_BAZA / 2 + 0.03;
  for (let k = 0; k < 5; k++) {
    const y = (k / 4 - 0.5) * 0.4 + (r() - 0.5) * 0.05;
    const lung = 0.35 + r() * 0.45;
    const leagan = (r() - 0.5) * 0.1;
    let p = new THREE.Vector3(0.1, y * 0.7, 0.06);
    const pasi = Math.max(5, Math.round((buza + lung) / 0.1));
    for (let i = 1; i <= pasi; i++) {
      const s = (i / pasi) * (buza - 0.1 + lung);
      // întâi pe orizontală până la buză, apoi pe verticală, lipit de fața de afară
      const q = s < buza - 0.1
        ? new THREE.Vector3(0.1 + s, y * (0.7 + 0.3 * s / (buza - 0.1)), 0.06 - 0.03 * (s / (buza - 0.1)))
        : new THREE.Vector3(buza + 0.01 * Math.sin(s * 9 + k), y + leagan * (s - buza + 0.1),
                            0.03 - (s - (buza - 0.1)));
      const dir = q.clone().sub(p).normalize();
      const lat = perp(dir, r);
      c.copy(jos_).lerp(sus_, 0.25 + r() * 0.65);
      // frunzele alternează stânga–dreapta pe lujer
      const lateral = dir.clone().multiplyScalar(0.35).addScaledVector(lat, i % 2 ? 0.9 : -0.9)
        .add(new THREE.Vector3(0.25, 0, -0.2)).normalize();
      m.frunza(p.clone().lerp(q, 0.5), lateral, perp(lateral, r), 0.085 + r() * 0.03, 0.06, c);
      p = q;
    }
  }
  return m.geometrie();
}

export function geometriePlanta(sp: SpeciePlanta): THREE.BufferGeometry {
  return sp === "tufa" ? geometrieTufa() : sp === "iarba" ? geometrieIarba() : geometrieCurgatoare();
}

// --------------------------------------------------------------------------- //
// așezarea într-o jardinieră
// --------------------------------------------------------------------------- //

/** Ce specii stau la rând într-o jardinieră, după `planting` din document. */
const RANDURI: Record<string, SpeciePlanta[]> = {
  mixed: ["tufa", "curgatoare", "iarba", "tufa", "curgatoare"],
  shrubs: ["tufa", "tufa", "tufa", "iarba"],
  grasses: ["iarba", "iarba", "tufa", "iarba"],
  hanging: ["curgatoare", "tufa", "curgatoare"],
};

/** Plantele unei bucăți de jardinieră: `a`–`b` e axul ei (fața de AFARĂ a
 *  jgheabului), `n` normala spre afară, `zSus` cota buzei, `gros` lățimea.
 *  `planting: "none"` (jgheab gol) nu dă nimic. */
export function plantePeJardiniera(a: number[], b: number[], n: number[], zSus: number, gros: number,
                                   planting: string | undefined): PlantaSit[] {
  const rand = RANDURI[planting ?? "mixed"];
  if (!rand) return [];
  const l = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (l < 0.3) return [];
  const ux = (b[0] - a[0]) / l, uy = (b[1] - a[1]) / l;
  const nr = Math.max(1, Math.round(l / PAS_PLANTE_M));
  const spreAfara = Math.atan2(n[1], n[0]);
  const out: PlantaSit[] = [];
  // decalajul rândului, ca două jardiniere vecine să nu înceapă cu aceeași plantă
  const start = Math.floor(hashLoc(a[0], a[1], zSus, 1) * rand.length);
  for (let i = 0; i < nr; i++) {
    const t = (i + 0.5) / nr;
    const cx = a[0] + ux * l * t - n[0] * gros / 2, cy = a[1] + uy * l * t - n[1] * gros / 2;
    const h1 = hashLoc(cx, cy, zSus, 2), h2 = hashLoc(cx, cy, zSus, 3), h3 = hashLoc(cx, cy, zSus, 4);
    const sp = rand[(start + i) % rand.length];
    const j = (h1 - 0.5) * 0.14;                       // tremur în lungul jgheabului
    out.push({
      x: cx + ux * j, y: cy + uy * j, z: zSus + PAMANT_PESTE_M - 0.01, sp,
      rot: sp === "curgatoare" ? spreAfara : h2 * Math.PI * 2,
      // tufa și iarba nu trec de lățimea jgheabului cu mai mult de o palmă
      s: (0.95 + h3 * 0.4) * (sp === "curgatoare" ? 1 : Math.min(1.25, Math.max(0.75, gros / 0.4))),
      gros: sp === "curgatoare" ? gros : undefined,
    });
  }
  return out;
}

/** PĂMÂNTUL din jgheab: o placă îngustă, retrasă 5 cm de la margini, cu fața
 *  de sus la `PAMANT_PESTE_M` peste buză — destul cât să nu stea în planul ei
 *  (două fețe orizontale la câțiva milimetri se bat de la distanță). */
export function geometriePamant(a: number[], b: number[], n: number[], zSus: number,
                                gros: number): THREE.BufferGeometry | null {
  const l = Math.hypot(b[0] - a[0], b[1] - a[1]);
  if (l < 0.3 || gros < 0.16) return null;
  const g = new THREE.BoxGeometry(l - 0.1, gros - 0.1, 0.06);
  g.deleteAttribute("uv");
  g.rotateZ(Math.atan2(b[1] - a[1], b[0] - a[0]));
  g.translate((a[0] + b[0]) / 2 - n[0] * gros / 2, (a[1] + b[1]) / 2 - n[1] * gros / 2,
              zSus + PAMANT_PESTE_M - 0.03);
  return g;
}

// --------------------------------------------------------------------------- //
// instanțiere
// --------------------------------------------------------------------------- //

export interface PlanteleMachetei {
  obiecte: THREE.Object3D[];
  deArs: { dispose(): void }[];
  peSpecii: Record<SpeciePlanta, number>;
}

const fara = () => { /* plantele nu se aleg cu mouse-ul */ };

/** Toate plantele scenei: o plasă instanțiată pe specie. */
export function construiestePlante(plante: PlantaSit[]): PlanteleMachetei {
  const deArs: { dispose(): void }[] = [];
  const obiecte: THREE.Object3D[] = [];
  const peSpecii = { tufa: 0, iarba: 0, curgatoare: 0 } as Record<SpeciePlanta, number>;
  if (!plante.length) return { obiecte, deArs, peSpecii };
  const mat = new THREE.MeshStandardMaterial({ vertexColors: true, flatShading: true,
                                               roughness: 0.85, side: THREE.DoubleSide });
  deArs.push(mat);
  const t = new THREE.Object3D();
  const tint = new THREE.Color();
  for (const sp of SPECII_PLANTE) {
    const lista = plante.filter((p) => p.sp === sp);
    peSpecii[sp] = lista.length;
    if (!lista.length) continue;
    const g = geometriePlanta(sp);
    deArs.push(g);
    const im = new THREE.InstancedMesh(g, mat, lista.length);
    // fără umbră proprie: trecerea de umbre le-ar desena a doua oară, iar umbra
    // unei frunze de 10 cm nu se vede pe machetă
    im.castShadow = false;
    im.raycast = fara;
    im.name = `plante:${sp}`;
    lista.forEach((p, i) => {
      t.position.set(p.x, p.y, p.z);
      t.rotation.set(0, 0, p.rot);
      // curgătoarea se întinde pe lățimea jgheabului ei (x local = spre afară)
      t.scale.set(p.s * (p.gros ? p.gros / GROS_DE_BAZA / p.s : 1), p.s, p.s);
      t.updateMatrix();
      im.setMatrixAt(i, t.matrix);
      const h = hashLoc(p.x, p.y, p.z, 5), l = hashLoc(p.x, p.y, p.z, 6);
      tint.setRGB(0.88 + h * 0.22, 0.9 + l * 0.18, 0.84 + (1 - h) * 0.22);
      im.setColorAt(i, tint);
    });
    im.instanceMatrix.needsUpdate = true;
    if (im.instanceColor) im.instanceColor.needsUpdate = true;
    obiecte.push(im);
  }
  return { obiecte, deArs, peSpecii };
}
